import React, { useEffect, useState, useRef } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/TableTags";
import { TextInput, NewNativeSelectInput, NumberInput } from "../ui/Input";
import useToaster from "../../hooks/useToaster";
import { debounce } from "lodash";
import { Popover, PopoverTrigger } from "../ui/Popover";
import DropdownCheckbox from "../ui/DropdownCheck";
import { FaCaretDown } from "react-icons/fa";
import { MdClose } from "react-icons/md";

const AddJournalForm = ({
  journalData,
  journalNames,
  oncancel,
  onSave,
  onchange,
  teams,
  journalManagers,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [fieldConfig, setFieldConfig] = useState(
    (journalData.fieldConfiguration || []).map((item) => ({
      ...item,
    }))
  );

  const modifiedFieldConfig = fieldConfig?.filter(
    (field) =>
      field.articleOrIssue === "article" || field.articleOrIssue === "both"
  );

  const [errors, setErrors] = useState({
    journalName: "",
    acronym: "",
    ceWorkflow: "",
    journalManager: "",
    team: "",
  });
  const formRef = useRef(null);

  let [popover, setPopover] = useState(false);
  let [selectedName, setSelectedName] = useState("");
  let [searchValue, setSearchValue] = useState("");
  let [isSubmit, setIsSubmit] = useState(false);

  const showToaster = useToaster();

  useEffect(() => {
    const formElement = formRef.current;

    const handleScroll = () => {
      if (popover) {
        setPopover(false);
      }
    };

    if (formElement) {
      formElement.addEventListener("scroll", handleScroll);
    }

    return () => {
      if (formElement) {
        formElement.removeEventListener("scroll", handleScroll);
      }
    };
  }, [popover]);

  useEffect(() => {
    const requiredKeys = ["journalName", "team", "journalManager", "url"];

    const hasEmptyRequired = requiredKeys.some((key) => {
      const value = journalData[key];
      return (
        value === null ||
        value === undefined ||
        (typeof value === "string" && value.trim() === "") ||
        (Array.isArray(value) && value.length === 0)
      );
    });

    const hasErrors = Object.values(errors).some((value) => value !== "");

    setIsSubmit(!hasErrors && !hasEmptyRequired);
  }, [errors, journalData]);

  useEffect(() => {
    if (
      journalData.journalManager.length > 0 &&
      typeof journalData.journalManager !== "string"
    ) {
      const names = journalData.journalManager
        .map((val) => {
          const match = journalManagers.find((j) => j.value === val);
          return match?.label?.split(" -")[0].trim();
        })
        .filter(Boolean);

      if (names.length === 1) {
        setSelectedName(names[0]);
      } else {
        const moreCount = names.length - 1;
        setSelectedName(`${names[0]} + ${moreCount} more`);
      }
    } else {
      setSelectedName("");
    }
  }, [journalManagers, journalData.journalManager]);

  const handleManagerChange = (selected) => {
    onchange({ target: { name: "journalManager", value: selected } });
  };

  useEffect(() => {
    const requiredKeys = ["journalName", "team", "journalManager"];

    const updatedErrors = { ...errors };
    let changed = false;

    for (const key of requiredKeys) {
      const value = journalData[key];

      const isEmpty =
        value === null ||
        value === undefined ||
        (typeof value === "string" && value.trim() === "") ||
        (Array.isArray(value) && value.length === 0);

      if (!isEmpty && errors[key]) {
        updatedErrors[key] = "";
        changed = true;
      }
    }

    if (changed) {
      setErrors(updatedErrors);
    }
  }, [journalData]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isSubmitting) return;

    if (!navigator.onLine) {
      showToaster("You are currently offline. Please check your internet connection!");
      return;
    }

    setIsSubmitting(true);

    try {
      const requiredFields = [
        { key: "journalName", label: "Journal" },
        { key: "team", label: "Team" },
        { key: "journalManager", label: "Production Editor(s)" },
        { key: "url", label: "URL" },
      ];

      const trimmedData = { ...journalData, fieldConfiguration: fieldConfig };

      let hasErrors = false;
      const newErrors = {};
      const missingFields = [];

      // Validate required fields
      requiredFields.forEach(({ key, label }) => {
        let value = journalData[key];

        if (typeof value === "string") {
          value = value.trim();
          trimmedData[key] = value;
        }

        const isEmpty =
          value === null ||
          value === undefined ||
          (typeof value === "string" && value === "") ||
          (Array.isArray(value) && value.length === 0);

        if (isEmpty) {
          newErrors[key] = "This field is required";
          missingFields.push(label);
          hasErrors = true;
        } else {
          newErrors[key] = "";
        }
      });

      setErrors(newErrors);

      if (hasErrors) {
        const fieldsText = missingFields.join(", ");
        const message =
          missingFields.length === 1
            ? `Please fill the required field: ${fieldsText}`
            : `Please fill the required fields: ${fieldsText}`;
        showToaster("Please fill in all the required fields correctly");
        return;
      }

      // Validate journal name uniqueness (only for new journals)
      const trimmedJournalName = trimmedData.journalName.trim().toLowerCase();
      if (
        !journalData.id &&
        journalNames.some((name) => name.toLowerCase() === trimmedJournalName)
      ) {
        setErrors((prev) => ({
          ...prev,
          journalName: "Journal name already exists",
        }));
        // showToaster(
        //   "Journal name already exists. Please choose a different name."
        // );
        return;
      }

      // Call the onSave function and wait for it to complete
      await onSave(e, trimmedData);
    } catch (error) {
      console.error("Error saving journal:", error);
      showToaster(
        "An error occurred while saving the journal. Please try again."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleToggle = (index) => {
    setFieldConfig((prev) =>
      prev.map((item, i) =>
        i === index
          ? {
              ...item,
              showField: !item.showField,
            }
          : item
      )
    );
  };

  const headers = [
    { key: "property", value: "FIELD NAME", width: "240px" },
    // { key: "articleOrIssue", value: "CATEGORY", width: "150px" },
    // { key: "fieldType", value: "DATA TYPE", width: "150px" },
    // { key: "method", value: "METHOD", width: "150px" },
    // { key: "manualOverwrite", value: "MANUAL OVERRIDE", width: "160px" },
    { key: "showField", value: "SHOW FIELD", width: "160px" },
  ];

  const hasFieldConfig = fieldConfig.length > 0;

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50 p-4">
      <div className="bg-white rounded-xl shadow-md max-w-[1088px] flex flex-col max-h-[90vh] w-full">
        <div className="flex justify-between items-center border-b border-gray-100 sticky top-0 bg-white z-10 h-[64px] px-6 py-3">
          <h1 className="font-bold text-[22px] text-[#35424D]">
            {journalData.id ? "Edit Journal" : "Add New Journal"}
          </h1>
          <button onClick={oncancel}>
            <MdClose className="text-[#35424D] w-[16px] h-[16px]" />
          </button>
        </div>

        <div ref={formRef} className="flex-1 overflow-y-auto px-6">
          <form className="h-full">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-4 pb-6">
              <div className="col-span-1 md:col-span-3">
                <h2 className="font-semibold text-[18px] h-[24px] text-[#5D6871]">
                  Journal Information
                </h2>
              </div>

              <TextInput
                label="Journal"
                name="journalName"
                value={journalData.journalName}
                onChange={onchange}
                required
                placeholder="Enter journal"
                error={errors.journalName}
                maxLength={100}
              />
              <div>
                <label className="block text-[#35424D] text-[16px]">
                  Production Editor(s) <span className="text-red-500">*</span>
                </label>
                <Popover
                  open={popover}
                  onOpenChange={(open) => {
                    if (!open) {
                      journalData.journalManager = [
                        ...journalData.journalManager,
                      ];
                    }
                    setPopover(open);
                  }}
                >
                  <PopoverTrigger asChild>
                    <div
                      className={`max-w-[310px] w-full h-[40px] p-2 mt-2 ${
                        errors.journalManager
                          ? "border-red-500"
                          : "border-[#C3C3C3]"
                      } border flex items-center justify-between rounded-md text-[#868E94] focus-within:border-[#35424D] focus-within:outline-none`}
                    >
                      <input
                        name="searchValue"
                        value={searchValue}
                        onChange={(e) =>
                          popover && setSearchValue(e.target.value)
                        }
                        className={`focus:outline-none h-[30px] w-[300px] focus`}
                        placeholder={
                          selectedName
                            ? selectedName
                            : "Select production editor(s)"
                        }
                      />
                      {searchValue.length <= 0 ? (
                        <FaCaretDown className={`text-gray-500 text-[18px] `} />
                      ) : (
                        <MdClose
                          className={`text-gray-500 text-[18px] `}
                          onClick={(e) => {
                            e.stopPropagation();
                            setSearchValue("");
                          }}
                        />
                      )}
                    </div>
                  </PopoverTrigger>
                  {errors.journalManager && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.journalManager}
                    </p>
                  )}
                  <DropdownCheckbox
                    options={journalManagers}
                    selectedValues={journalData.journalManager}
                    onSelectionChange={handleManagerChange}
                    onClose={() => setPopover(false)}
                    width="w-[310px]"
                    searchBar={false}
                    search={searchValue.trim()}
                    setSearch={setSearchValue}
                    label={false}
                    type="Journal"
                  />
                </Popover>
              </div>
              <NewNativeSelectInput
                label="Team"
                name="team"
                value={journalData.team}
                onChange={onchange}
                options={teams}
                required
                placeholder="Select team"
                error={errors.team}
              />
              <TextInput
                label="URL Abbreviation"
                name="url"
                value={journalData.url}
                onChange={onchange}
                required
                placeholder="Enter Journal Url"
                maxLength={200}
              />
              <div className="col-span-1 md:col-span-3">
                <h2 className="font-semibold text-[18px] h-[24px] text-[#5D6871]">
                  Journal Configuration
                </h2>
              </div>
              <NumberInput
                label="Cover subs due"
                name="coverSubInvitees"
                value={journalData.coverSubInvitees}
                onChange={onchange}
                placeholder="No. of days before publishing"
                maxValue={99}
              />
              <NumberInput
                label="Cover due to TNQ"
                name="coverSubmissionDue"
                value={journalData.coverSubmissionDue}
                onChange={onchange}
                placeholder="No. of days before publishing"
                maxValue={99}
              />
              <NumberInput
                label="Issue holdout"
                name="issueHoldout"
                value={journalData.issueHoldout}
                onChange={onchange}
                placeholder="No. of days before publishing"
                maxValue={99}
              />
              <NumberInput
                label="Last MS for SCE"
                name="lastMsForRed"
                value={journalData.lastMsForRed}
                onChange={onchange}
                placeholder="No. of days before publishing"
                maxValue={99}
              />
              <NumberInput
                label="Issue finalizing"
                name="issueFinalizing"
                value={journalData.issueFinalizing}
                onChange={onchange}
                placeholder="No. of days before publishing"
                maxValue={99}
              />
              <NumberInput
                label="st@r form due"
                name="noOfDayBeforeStarFromDue"
                value={journalData.noOfDayBeforeStarFromDue}
                onChange={onchange}
                placeholder="No. of days before publishing"
                maxValue={99}
              />
            </div>

            {hasFieldConfig && (
              <>
                <h2 className="font-semibold text-[#5D6871] text-[18px] h-[24px] mt-5 mb-4">
                  Field Configuration
                </h2>
                <div className="overflow-x-auto rounded-sm mb-6">
                  <Table className="w-full bg-white border border-[#D7D9DB] border-collapse">
                    <TableHeader className="bg-[#F8F8F8]">
                      <TableRow>
                        {Array.from({ length: 3 }).map((_, index) => (
                          <React.Fragment key={index}>
                            <TableHead className="h-[40px] border-b border-l border-[#D7D9DB] px-4 py-2 text-[11px] font-semibold text-left text-[#35424D]">
                              FIELD NAME
                            </TableHead>
                            <TableHead className="h-[40px] border-b border-l border-[#D7D9DB] px-4 py-2 text-[11px] font-semibold text-left text-[#35424D]">
                              SHOW FIELD
                            </TableHead>
                          </React.Fragment>
                        ))}
                      </TableRow>
                    </TableHeader>
                    <TableBody className="text-[#35424D] divide-y divide-gray-200">
                      {Array.from({
                        length: Math.ceil(modifiedFieldConfig.length / 3),
                      }).map((_, rowIndex) => {
                        const start = rowIndex * 3;
                        const fieldsChunk = modifiedFieldConfig.slice(
                          start,
                          start + 3
                        );

                        return (
                          <TableRow key={rowIndex} className="hover:bg-gray-50">
                            {fieldsChunk.map((field, colIndex) => (
                              <React.Fragment key={colIndex}>
                                <TableCell className="py-3 px-4 font-semibold text-[13px] border-r border-b border-[#D7D9DB]">
                                  {field.property}
                                </TableCell>
                                <TableCell className="py-3 px-4 border-r border-b border-[#D7D9DB]">
                                  <button
                                    type="button"
                                    onClick={() =>
                                      handleToggle(start + colIndex)
                                    }
                                    className={`w-8 h-4 rounded-full p-0.5 flex items-center transition-colors ${
                                      field.showField
                                        ? "bg-blue-500"
                                        : "bg-gray-300"
                                    }`}
                                  >
                                    <span
                                      className={`block w-3.5 h-3.5 bg-white rounded-full shadow-md transition-transform ${
                                        field.showField
                                          ? "translate-x-[16px]"
                                          : "translate-x-0"
                                      }`}
                                    />
                                  </button>
                                </TableCell>
                              </React.Fragment>
                            ))}

                            {/* Fill empty cells if last row has fewer than 3 fields */}
                            {fieldsChunk.length < 3 &&
                              Array.from({
                                length: 3 - fieldsChunk.length,
                              }).map((_, emptyIndex) => (
                                <React.Fragment key={emptyIndex}>
                                  <TableCell className="py-3 px-4 border-r border-b border-[#D7D9DB]"></TableCell>
                                  <TableCell className="py-3 px-4 border-r border-b border-[#D7D9DB]"></TableCell>
                                </React.Fragment>
                              ))}
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              </>
            )}
          </form>
        </div>

        <div className="flex justify-end space-x-6 border-t py-4 border-gray-100 bg-white sticky bottom-0 h-[64px] px-6">
          <button
            type="button"
            onClick={handleSubmit}
            disabled={isSubmitting || !isSubmit}
            className={`min-w-[105px] px-3 h-[36px] ${
              isSubmit ? "bg-[#1C40CA]" : "opacity-50 bg-gray-400"
            }  text-white rounded`}
          >
            {journalData.id ? "Update Journal" : "Create Journal"}
          </button>
        </div>
      </div>
    </div>
  );
};

export { AddJournalForm };
